import React, { useState, useEffect } from "react";
import AddUserModal from "./AddUserModal";
import EditUserModal from "./EditUserModal";
import GenerateReportModal from "./GenerateReportModal";
import { IoPersonAddOutline, IoSearchOutline } from "react-icons/io5";
import { LiaUserEditSolid, LiaEye } from "react-icons/lia";
import { TiDeleteOutline } from "react-icons/ti";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Pagination from "../../Pagination";
import Swal from "sweetalert2";
import { BsFiletypeCsv, BsThreeDotsVertical } from "react-icons/bs";
import { FiUser, FiMail, FiShield, FiActivity, FiStar, FiFilter, FiX } from "react-icons/fi";
import apiClient from '../../../api/apiClient';

function UsersList({ viewMode = "cards" }) {
  const { token } = useSelector((state) => state.auth);
  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const getUsers = async () => {
    setIsLoading(true);
    try {
      const url = `/api/dashboard/search-users/?search=${searchQuery}&page=${currentPage}`;
      const response = await apiClient.get(url);
      setUsers(response.data.results);
      setCurrentPage(response.data.current_page);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error("Error fetching users:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch users. Please try again.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // const deleteUser = async (email) => {
  //   try {
  //     const response = await apiClient.delete(
  //       `/api/users/delete-user/`,
  //       {
  //         data: { email },
  //       }
  //     );
  //     if (response.status === 200) {
  //       refreshUsers();
  //       Swal.fire({
  //         icon: "success",
  //         title: "Deleted!",
  //         text: response.data.message,
  //         toast: true,
  //         position: "top-end",
  //         showConfirmButton: false,
  //         timer: 3000,
  //       });
  //     }
  //   } catch (error) {
  //     console.error("Error deleting user:", error);
  //     Swal.fire(
  //       "Error!",
  //       error.response?.data?.error || "An error occurred",
  //       "error"
  //     );
  //   }
  // };

  // const handleDeleteUser = (email) => {
  //   Swal.fire({
  //     title: "Are you sure?",
  //     text: `You are about to delete the user with email: ${email}. This action cannot be undone.`,
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonColor: "#3085d6",
  //     cancelButtonColor: "#d33",
  //     confirmButtonText: "Yes, delete it!",
  //   }).then((result) => {
  //     if (result.isConfirmed) {
  //       deleteUser(email);
  //     }
  //   });
  // };

  const navigate = useNavigate();
  const viewPerformance = (id) => {
    navigate(`/users-perfomance/${id}`);
  };
  
  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedUser) => {
    try {
      const url = `/api/users/edit-user/${updatedUser.id}/`;
      const response = await apiClient.put(url, updatedUser);

      if (response.status === 200) {
        Swal.fire({
          icon: "success",
          title: "User Updated",
          text: `User ${updatedUser.email} updated successfully.`,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });

        refreshUsers();
        setIsEditModalOpen(false);
      }
    } catch (error) {
      console.error("Error updating user:", error);
      Swal.fire(
        "Error!",
        error.response?.data?.error || "An error occurred",
        "error"
      );
    }
  };

  const refreshUsers = () => {
    getUsers();
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = `${user.first_name} ${user.last_name} ${user.email}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === "all" || user.role.toLowerCase() === selectedRole;
    const matchesStatus = selectedStatus === "all" || user.status === selectedStatus;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    const colors = {
      "Admin": "bg-gradient-to-r from-green-500 to-teal-500 text-white",
      "Reviewer": "bg-gradient-to-r from-blue-500 to-cyan-500 text-white",
      "User": "bg-gradient-to-r from-orange-500 to-amber-500 text-white"
    };
    return colors[role] || "bg-gray-500 text-white";
  };

  // Get status color
  const getStatusColor = (status) => {
    return status === "active" ? "text-green-500" : "text-gray-400";
  };

  // Generate user initials for avatar
  const getUserInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  useEffect(() => {
    getUsers();
  }, [searchQuery, currentPage]);

  // Loading skeleton for cards
  const LoadingCard = () => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden animate-pulse">
      <div className="h-16 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700"></div>
      <div className="pt-8 p-6">
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded mb-2"></div>
        <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded mb-4 w-3/4"></div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="h-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
          <div className="h-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
        </div>
        <div className="flex gap-2">
          <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded flex-1"></div>
          <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded flex-1"></div>
          <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-10"></div>
        </div>
      </div>
    </div>
  );

  const UserCard = ({ user }) => (
    <div className="group bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm hover:shadow-2xl border border-white/20 dark:border-gray-700/30 transition-all duration-300 hover:scale-[1.02] overflow-hidden">
      {/* Card Header with Gradient */}
      <div className="h-16 bg-gradient-to-r from-green-500 to-teal-500 relative">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute -bottom-6 left-6">
          <div className="w-12 h-12 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-lg border-4 border-white dark:border-gray-800">
            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
              {getUserInitials(user.first_name, user.last_name)}
            </span>
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="pt-8 p-6">
        {/* User Info */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {user.first_name} {user.last_name}
            </h3>
            <div className={`w-3 h-3 rounded-full ${getStatusColor(user.status || 'active')}`}></div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center mb-2">
            <FiMail className="w-4 h-4 mr-2" />
            {user.email}
          </p>
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
            {user.role}
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-sm rounded-lg p-3 border border-blue-200/30 dark:border-blue-700/30">
            <div className="flex items-center justify-between">
              <FiActivity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Attempts</span>
            </div>
            <p className="text-xl font-bold text-blue-700 dark:text-blue-300 mt-1">{user.attempts || 0}</p>
          </div>
          <div className="bg-gradient-to-r from-emerald-50/50 to-green-50/50 dark:from-emerald-900/20 dark:to-green-900/20 backdrop-blur-sm rounded-lg p-3 border border-emerald-200/30 dark:border-emerald-700/30">
            <div className="flex items-center justify-between">
              <FiStar className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Reviews</span>
            </div>
            <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">{user.reviews || 0}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => handleEditUser(user)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-emerald-100/70 hover:bg-emerald-200/70 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 rounded-lg transition-colors text-sm font-medium backdrop-blur-sm"
          >
            <LiaUserEditSolid className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => viewPerformance(user.id)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-100/70 hover:bg-blue-200/70 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded-lg transition-colors text-sm font-medium backdrop-blur-sm"
          >
            <LiaEye className="w-4 h-4" />
            View
          </button>
          {/* <button
            onClick={() => handleDeleteUser(user.email)}
            className="px-3 py-2 bg-red-100/70 hover:bg-red-200/70 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg transition-colors backdrop-blur-sm"
          >
            <TiDeleteOutline className="w-4 h-4" />
          </button> */}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Search and Filters Bar */}
      <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <IoSearchOutline className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200/50 dark:border-gray-600/50 rounded-xl bg-gray-50/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 ${
                showFilters
                  ? "bg-gradient-to-r from-green-500 to-teal-500 text-white shadow-md"
                  : "bg-gray-100/70 hover:bg-gray-200/70 dark:bg-gray-700/50 dark:hover:bg-gray-600/50 text-gray-700 dark:text-gray-300"
              } backdrop-blur-sm`}
            >
              <FiFilter className="w-4 h-4" />
              <span className="text-sm font-medium">Filters</span>
            </button>

            <button
              onClick={() => setIsGenerateReportModalOpen(true)}
              className="flex items-center gap-2 px-4 py-3 bg-gray-100/70 hover:bg-gray-200/70 dark:bg-gray-700/50 dark:hover:bg-gray-600/50 text-gray-700 dark:text-gray-300 rounded-xl transition-all backdrop-blur-sm"
            >
              <BsFiletypeCsv className="w-4 h-4" />
              <span className="text-sm font-medium">Export</span>
            </button>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white rounded-xl transition-all shadow-lg hover:shadow-sm"
            >
              <IoPersonAddOutline className="w-4 h-4" />
              <span className="text-sm font-medium">Add User</span>
            </button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200/50 dark:border-gray-600/50">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <FiFilter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
              </div>
              
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="px-4 py-2 border border-gray-200/50 dark:border-gray-600/50 rounded-lg bg-gray-50/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="reviewer">Reviewer</option>
                <option value="tasker">Tasker</option>
              </select>

              {/* <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-gray-200/50 dark:border-gray-600/50 rounded-lg bg-gray-50/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select> */}

              {(selectedRole !== "all" || selectedStatus !== "all") && (
                <button
                  onClick={() => {
                    setSelectedRole("all");
                    setSelectedStatus("all");
                  }}
                  className="flex items-center gap-1 px-3 py-2 bg-red-100/70 hover:bg-red-200/70 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg transition-colors text-sm backdrop-blur-sm"
                >
                  <FiX className="w-3 h-3" />
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Results Summary */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredUsers.length} of {users.length} users
          </p>
        </div>
      </div>

      {/* Users Content */}
      {isLoading ? (
        viewMode === "cards" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <LoadingCard key={i} />
            ))}
          </div>
        ) : (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 overflow-hidden">
            <div className="animate-pulse p-6">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/4"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      ) : filteredUsers.length === 0 ? (
        /* Empty State */
        <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 p-12 text-center">
          <FiUser className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No users found
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {searchQuery || selectedRole !== "all" || selectedStatus !== "all" 
              ? "Try adjusting your search or filter criteria"
              : "Get started by adding your first user"
            }
          </p>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white rounded-xl transition-all shadow-lg hover:shadow-sm"
          >
            <IoPersonAddOutline className="w-4 h-4" />
            Add New User
          </button>
        </div>
      ) : viewMode === "cards" ? (
        /* Cards View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map((user) => (
            <UserCard key={user.id} user={user} />
          ))}
        </div>
      ) : (
        /* Enhanced Table View */
        <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-700/50 dark:to-gray-800/50 backdrop-blur-sm">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/50 dark:divide-gray-600/50">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-teal-500 flex items-center justify-center text-white font-medium text-sm mr-3">
                          {getUserInitials(user.first_name, user.last_name)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {user.attempts || 0} attempts • {user.reviews || 0} reviews
                      </div>
                      {/* <div className="text-sm text-gray-500 dark:text-gray-400">
                        Last active: Recently
                      </div> */}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-2 ${(user.status || 'active') === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                        <span className={`text-sm capitalize ${getStatusColor(user.status || 'active')}`}>
                          {user.status || 'active'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-2 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors rounded-lg hover:bg-emerald-100/50 dark:hover:bg-emerald-900/30"
                          title="Edit User"
                        >
                          <LiaUserEditSolid className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => viewPerformance(user.id)}
                          className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors rounded-lg hover:bg-blue-100/50 dark:hover:bg-blue-900/30"
                          title="View Performance"
                        >
                          <LiaEye className="w-4 h-4" />
                        </button>
                        {/* <button
                          onClick={() => handleDeleteUser(user.email)}
                          className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors rounded-lg hover:bg-red-100/50 dark:hover:bg-red-900/30"
                          title="Delete User"
                        >
                          <TiDeleteOutline className="w-4 h-4" />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && filteredUsers.length > 0 && (
        <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, users.length)} of {users.length} results
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </div>
        </div>
      )}

      {/* Modals */}
      <AddUserModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        refreshUsers={refreshUsers}
      />
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={editingUser}
        onEdit={handleSaveEdit}
      />
      <GenerateReportModal
        isOpen={isGenerateReportModalOpen}
        onClose={() => setIsGenerateReportModalOpen(false)}
      />
    </div>
  );
}

export default UsersList;